import type { FC, ReactNode } from 'react'

import { Bot, Braces, Workflow } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { ArrowLink } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { FlowStepsPanel } from '@/components/product/flow-steps-panel'
import { PilotMiniChat } from '@/components/product/pilot-mini-chat'
import { ApiPanel } from '@/components/product/api-panel'

interface CardHeaderProps {
    icon: typeof Workflow
    name: string
    text: string
}

const CardHeader: FC<CardHeaderProps> = ({ icon: Icon, name, text }): ReactNode => {
    return (
        <div>
            <span className='grid size-11 place-items-center rounded-xl bg-cream text-ink transition-colors duration-500 group-hover/card:bg-ultramarine-soft group-hover/card:text-ultramarine'>
                <Icon aria-hidden='true' className='size-5' strokeWidth={1.6} />
            </span>

            <h3 className='mt-6 font-heading text-2xl font-semibold tracking-[-0.03em] text-ink sm:text-[28px]'>
                {name}
            </h3>

            <p className='mt-2.5 max-w-md text-[15px] leading-relaxed text-ink-soft sm:text-base'>
                {text}
            </p>
        </div>
    )
}

const CARD = 'surface card-warm group/card relative isolate flex h-full flex-col overflow-hidden rounded-[24px] border border-transparent p-5 sm:rounded-[28px] sm:p-9'

/** Bento grid: one large Managed Flows card beside stacked Pilot and Open API cards. */
export const WhatWeDo: FC = (): ReactNode => {
    return (
        <section id='what-we-do' aria-labelledby='what-we-do-title' className='section-y relative'>
            <div className='shell'>
                <SectionHeading
                    id='what-we-do-title'
                    eyebrow="The platform"
                    title="What Quayside does"
                    intro="Quayside connects carriers, ports, customs systems and your own software, then builds and runs the workflows between them. One team moves your shipment data reliably and removes the repetitive work from your supply chain."
                />

                <div className='mt-12 grid gap-4 *:min-w-0 sm:gap-5 lg:mt-16 lg:grid-cols-5 lg:grid-rows-[auto_auto]'>
                    <Reveal className='lg:col-span-3 lg:row-span-2'>
                        <article className={CARD}>
                            <div aria-hidden='true' className='pointer-events-none absolute -right-24 -top-24 -z-10 size-80 rounded-full bg-[radial-gradient(closest-side,rgba(255,184,156,0.45),transparent)]' />

                            <CardHeader
                                icon={Workflow}
                                name="Managed Flows"
                                text="Quayside specialists scope, build, launch, monitor and maintain your production workflows."
                            />

                            <FlowStepsPanel className='mt-9 lg:mt-auto' />

                            <ArrowLink href='/platform#managed-flows' className='mt-8'>
                                How Managed Flows work
                            </ArrowLink>
                        </article>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-2'>
                        <article className={CARD}>
                            <div aria-hidden='true' className='pointer-events-none absolute -bottom-28 -right-16 -z-10 size-72 rounded-full bg-[radial-gradient(closest-side,rgba(201,187,255,0.45),transparent)]' />

                            <CardHeader
                                icon={Bot}
                                name="Pilot"
                                text="Connects to your ops tools, learns how your team works, and handles repetitive tasks in plain language."
                            />

                            <PilotMiniChat className='mt-7' />

                            <ArrowLink href='/platform#pilot' className='mt-7'>
                                Meet Pilot
                            </ArrowLink>
                        </article>
                    </Reveal>

                    <Reveal delay={200} className='lg:col-span-2'>
                        <article className={CARD}>
                            <div aria-hidden='true' className='pointer-events-none absolute -bottom-28 -left-16 -z-10 size-72 rounded-full bg-[radial-gradient(closest-side,rgba(143,201,255,0.4),transparent)]' />

                            <CardHeader
                                icon={Braces}
                                name="Open API"
                                text="One key. Read-only, run-flows or full access."
                            />

                            <ApiPanel className='mt-7' />

                            <ArrowLink href='/platform#open-api' className='mt-7'>
                                Read about the API
                            </ArrowLink>
                        </article>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
